/**
 * GameChanger CSV batting section schema.
 * Defines column names from GameChanger season stats export and maps them to app stat_key.
 * appStatKey null = no mapping in app (ignored on import).
 */

export interface GameChangerBattingColumn {
  /** Column header as it appears in GameChanger CSV (row 2) */
  gcHeader: string;
  /** Our sport_stats stat_key, or null to ignore */
  appStatKey: string | null;
  description?: string;
}

export const GAMECHANGER_BATTING_SCHEMA: GameChangerBattingColumn[] = [
  { gcHeader: 'GP', appStatKey: null, description: 'Games played' },
  { gcHeader: 'PA', appStatKey: null, description: 'Plate appearances' },
  { gcHeader: 'AB', appStatKey: 'AB', description: 'At bats' },
  { gcHeader: 'AVG', appStatKey: null, description: 'Batting average (calculated)' },
  { gcHeader: 'OBP', appStatKey: null, description: 'On-base percentage (calculated)' },
  { gcHeader: 'OPS', appStatKey: null, description: 'OPS (calculated)' },
  { gcHeader: 'SLG', appStatKey: null, description: 'Slugging (calculated)' },
  { gcHeader: 'H', appStatKey: 'H', description: 'Hits' },
  { gcHeader: '1B', appStatKey: '1B', description: 'Singles' },
  { gcHeader: '2B', appStatKey: '2B', description: 'Doubles' },
  { gcHeader: '3B', appStatKey: '3B', description: 'Triples' },
  { gcHeader: 'HR', appStatKey: 'HR', description: 'Home runs' },
  { gcHeader: 'RBI', appStatKey: 'RBI', description: 'Runs batted in' },
  { gcHeader: 'R', appStatKey: 'R', description: 'Runs scored' },
  { gcHeader: 'BB', appStatKey: 'BB', description: 'Walks' },
  { gcHeader: 'SO', appStatKey: 'SO', description: 'Strikeouts' },
  { gcHeader: 'K-L', appStatKey: null, description: 'Strikeouts looking' },
  { gcHeader: 'HBP', appStatKey: 'HBP', description: 'Hit by pitch' },
  { gcHeader: 'SAC', appStatKey: null, description: 'Sacrifice hits & bunts' },
  { gcHeader: 'SF', appStatKey: 'SF', description: 'Sacrifice flies' },
  { gcHeader: 'ROE', appStatKey: null, description: 'Reached on error' },
  { gcHeader: 'FC', appStatKey: 'FC', description: "Fielder's choice" },
  { gcHeader: 'SB', appStatKey: 'SB', description: 'Stolen bases' },
  { gcHeader: 'SB%', appStatKey: null, description: 'Stolen base percentage' },
  { gcHeader: 'CS', appStatKey: null, description: 'Caught stealing' },
  { gcHeader: 'PIK', appStatKey: null, description: 'Picked off' },
  { gcHeader: 'QAB', appStatKey: null, description: 'Quality at bats' },
  { gcHeader: 'QAB%', appStatKey: null, description: 'Quality at bats %' },
  { gcHeader: 'PA/BB', appStatKey: null, description: 'Plate appearances per walk' },
  { gcHeader: 'BB/K', appStatKey: null, description: 'Walks per strikeout' },
  { gcHeader: 'C%', appStatKey: null, description: 'Contact percentage' },
  { gcHeader: 'HHB', appStatKey: null, description: 'Hard hit balls' },
  { gcHeader: 'LD%', appStatKey: null, description: 'Line drive %' },
  { gcHeader: 'FB%', appStatKey: null, description: 'Fly ball %' },
  { gcHeader: 'GB%', appStatKey: null, description: 'Ground ball %' },
  { gcHeader: 'BABIP', appStatKey: null, description: 'Batting average on balls in play' },
  { gcHeader: 'BA/RISP', appStatKey: null, description: 'BA with runners in scoring position' },
  { gcHeader: 'LOB', appStatKey: null, description: 'Left on base' },
  { gcHeader: '2OUTRBI', appStatKey: null, description: '2-out RBI' },
  { gcHeader: 'XBH', appStatKey: null, description: 'Extra-base hits' },
  { gcHeader: 'TB', appStatKey: null, description: 'Total bases' },
  { gcHeader: 'PS', appStatKey: null, description: 'Pitches seen' },
  { gcHeader: 'PS/PA', appStatKey: null, description: 'Pitches seen per PA' },
  { gcHeader: '2S+3', appStatKey: null, description: 'PA with 3+ pitches after 2 strikes' },
  { gcHeader: '2S+3%', appStatKey: null, description: '% of PA 3+ pitches after 2 strikes' },
  { gcHeader: '6+', appStatKey: null, description: 'PA with 6+ pitches' },
  { gcHeader: '6+%', appStatKey: null, description: '% of PA 6+ pitches' },
  { gcHeader: 'AB/HR', appStatKey: null, description: 'At bats per home run' },
  { gcHeader: 'GIDP', appStatKey: null, description: 'Hit into double play' },
  { gcHeader: 'GITP', appStatKey: null, description: 'Hit into triple play' },
  { gcHeader: 'CI', appStatKey: null, description: "Catcher's interference" },
];
